<script setup>
/**
 * PdfPreview
 *
 * Inline PDF preview using the browser's own native PDF renderer (an
 * <iframe> pointed at the file) rather than a new client-side dependency
 * like pdf.js - Chrome, Firefox, Edge, and Safari all ship a built-in PDF
 * viewer (complete with its own zoom/page-nav/search/print/download
 * toolbar) when a PDF is loaded in an iframe, so embedding one gets a real
 * preview "for free". Chatwoot didn't have any PDF-specific handling
 * before this - PDFs fell through to the generic file/download card.
 *
 * Not every environment can render this (some in-app webviews, older
 * mobile browsers) - a load timeout falls back to a plain "preview
 * unavailable, download to view" state rather than showing a blank frame.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import MediaSkeleton from 'dashboard/components-next/message/media/MediaSkeleton.vue';
import Icon from 'next/icon/Icon.vue';

defineProps({
  src: { type: String, required: true },
  height: { type: String, default: '20rem' },
});

const LOAD_TIMEOUT_MS = 6000;

const isLoaded = ref(false);
const hasFailed = ref(false);
let timeoutId = null;

const onIframeLoad = () => {
  isLoaded.value = true;
  clearTimeout(timeoutId);
};

onMounted(() => {
  timeoutId = setTimeout(() => {
    if (!isLoaded.value) hasFailed.value = true;
  }, LOAD_TIMEOUT_MS);
});

onBeforeUnmount(() => clearTimeout(timeoutId));
</script>

<template>
  <div
    class="relative w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-2"
    :style="{ height }"
  >
    <MediaSkeleton
      v-if="!isLoaded && !hasFailed"
      icon="i-lucide-file-text"
      :rounded="false"
      class="absolute inset-0 h-full"
    />
    <div
      v-if="hasFailed"
      class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-n-slate-11"
    >
      <Icon icon="i-lucide-file-text" class="size-8" />
      <p class="mb-0 text-sm">
        {{ $t('COMPONENTS.MEDIA.PDF_PREVIEW_UNAVAILABLE') }}
      </p>
    </div>
    <iframe
      v-show="isLoaded"
      :src="src"
      :title="$t('COMPONENTS.MEDIA.PDF_LOADING')"
      class="w-full h-full border-0"
      @load="onIframeLoad"
    />
  </div>
</template>
